import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { ENTRY_PLAN, PLANS } from "@/data/site";

const LIFETIME = PLANS.find((p) => p.key === "lifetime")!;

export const StickyPricingBar = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  // Check payment status from localStorage synchronously - no Supabase call
  const hasPaid = localStorage.getItem('payment_completed') === 'true' || !!localStorage.getItem('payment_plan');

  useEffect(() => {
    let ticking = false;
    let cachedPageHeight = 0;
    
    // Defer initial measurement to avoid forced reflow on mount
    requestAnimationFrame(() => {
      cachedPageHeight = document.documentElement.scrollHeight - window.innerHeight;
    });
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollPosition = window.scrollY;
          const halfwayPoint = cachedPageHeight / 2;
          setIsVisible(scrollPosition > halfwayPoint);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    const handleResize = () => {
      requestAnimationFrame(() => {
        cachedPageHeight = document.documentElement.scrollHeight - window.innerHeight;
        const scrollPosition = window.scrollY;
        const halfwayPoint = cachedPageHeight / 2;
        setIsVisible(scrollPosition > halfwayPoint);
      });
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const scrollToPricing = () => {
    const pricingSection = document.getElementById("pricing-section");
    if (pricingSection) {
      pricingSection.scrollIntoView({
        behavior: "smooth"
      });
    }
  };

  // Don't show the bar if user has already paid
  if (hasPaid) {
    return null;
  }

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-50 transition-all duration-500 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}`}>
      <div className="relative bg-gradient-primary px-3 py-2.5 sm:px-4 sm:py-3 shadow-[0_8px_32px_rgba(255,107,0,0.4),0_16px_48px_rgba(255,107,0,0.2),0_24px_64px_rgba(0,0,0,0.3)] rounded-t-2xl sm:rounded-2xl sm:mx-4 sm:my-2 md:mx-6 md:my-3 before:absolute before:inset-0 before:rounded-t-2xl sm:before:rounded-2xl before:bg-gradient-to-b before:from-white/20 before:to-transparent before:pointer-events-none after:absolute after:inset-[1px] after:rounded-t-2xl sm:after:rounded-2xl after:border after:border-white/30 after:pointer-events-none hover:shadow-[0_12px_40px_rgba(255,107,0,0.5),0_20px_56px_rgba(255,107,0,0.3),0_28px_72px_rgba(0,0,0,0.4)] transition-all duration-300">
        <div className="container mx-auto relative z-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            {/* Left Content */}
            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 w-full sm:w-auto">
              <div className="relative flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-lg backdrop-blur-sm flex items-center justify-center shadow-[0_4px_16px_rgba(0,0,0,0.3)] before:absolute before:inset-0 before:rounded-lg before:bg-gradient-to-b before:from-white/30 before:to-transparent before:pointer-events-none after:absolute after:inset-[1px] after:rounded-lg after:border after:border-white/40 after:pointer-events-none">
                <span className="text-xl sm:text-2xl font-black text-white relative z-10">AI</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-xs sm:text-sm md:text-base lg:text-lg truncate drop-shadow-lg">
                  AI Automation Base — full membership
                </h3>
                {/* Read from src/data/site.ts, the same array the pricing table renders.
                    This bar used to read "$50 / Lifetime  $129": $50 is the yearly price,
                    lifetime is $99, and $129 was not a price anywhere on the site. */}
                <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5 sm:mt-1">
                  <span className="text-white font-bold text-base sm:text-lg md:text-xl drop-shadow-lg">
                    From ${ENTRY_PLAN.amount}/month
                  </span>
                  <span className="text-white/80 text-xs sm:text-sm drop-shadow-md">
                    or ${LIFETIME.amount} once
                  </span>
                </div>
              </div>
            </div>

            {/* Right Button */}
            <Button onClick={scrollToPricing} className="relative bg-[#1a2332] hover:bg-black text-white border-2 border-black px-6 py-4 sm:px-8 sm:py-5 md:py-6 font-semibold rounded-full shadow-[0_8px_24px_rgba(0,0,0,0.4),0_4px_8px_rgba(0,0,0,0.3)] hover:shadow-[0_12px_32px_rgba(0,0,0,0.5),0_6px_12px_rgba(0,0,0,0.4)] transition-all duration-300 hover:scale-105 active:scale-95 flex-shrink-0 text-sm sm:text-base md:text-lg w-full sm:w-auto before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-b before:from-white/10 before:to-transparent before:pointer-events-none after:absolute after:inset-[2px] after:rounded-full after:border after:border-white/20 after:pointer-events-none">Get Started</Button>
          </div>
        </div>
      </div>
    </div>
  );
};
